import { getQuestionList, getAnswered } from '@ugc-api/question';
import { getThumbStatus } from '@ugc-api/helpful-status';

import state from '@ugc-store';
import { toProfileImageUrl, getProductIdFromRoute, getUrlNameFromRoute, addDataLayer } from '@ugc-utils/utils';
import { IQuestionInfo, IQuestionList, UserType } from './interfaces';

export const toAnswerShareUrl = aid => {
  const productId = getProductIdFromRoute();
  const urlName = getUrlNameFromRoute();
  return `https://${window.location.host}/qna/${urlName}/${productId}/a/${aid}`.toLowerCase();
};
export const toQuestionShareUrl = qid => {
  const productId = getProductIdFromRoute();
  const urlName = getUrlNameFromRoute();
  return `https://${window.location.host}/qna/${urlName}/${productId}/q/${qid}`.toLowerCase();
};

export const toAnswerInfo = answer => {
  if (answer) {
    const {
      id = '',
      questionId = '',
      userType = UserType.Customer,
      content = '',
      customerNickname = '',
      customerProfileLink = '',
      postedDateLocalized = '',
      hasRewarded = false,
      hasThumbsUp = null,
      helpfulNo = 0,
      helpfulYes = 0,
      verifiedPurchase = false,
      isBest = false,
      profileInfo = {},
    } = answer;
    return {
      id,
      questionId,
      userType,
      content,
      customerNickname,
      customerProfileLink,
      postedDateLocalized,
      hasRewarded,
      hasThumbsUp,
      helpfulYes,
      helpfulNo,
      verifiedPurchase,
      isBest,
      profileImageUrl: toProfileImageUrl(profileInfo),
      answerShareUrl: toAnswerShareUrl(id),
    };
  }
  return null;
};

export const toQuestionInfo = question => {
  const {
    id = '',
    content = '',
    postedDateLocalized = '',
    answerCount = 0,
    answers = [],
    customerServiceAnswer = null,
    vendorAnswer = null,
  } = question;

  const answersIdList = [];
  const answersDetail = {};

  if (answers.length) {
    answers.forEach(answer => {
      const answerRes = toAnswerInfo(answer);
      answersDetail[answerRes.id] = answerRes;
      answersIdList.push(answerRes.id);
    });
  }

  if (customerServiceAnswer) {
    answersIdList.push(customerServiceAnswer.id);
    answersDetail[customerServiceAnswer.id] = toAnswerInfo(customerServiceAnswer)
  }
  if (vendorAnswer) {
    answersIdList.push(vendorAnswer.id);
    answersDetail[vendorAnswer.id] = toAnswerInfo(vendorAnswer)
  }

  return {
    id,
    content,
    postedDateLocalized,
    answerCount,
    questionShareUrl: toQuestionShareUrl(id),
    answersIdList,
    answersDetail,
  };
};

export const toQuestionInfoList = questionList => {
  const qids = [];
  const questionBreakUpById = {};
  if (questionList.length) {
    questionList.forEach(question => {
      qids.push(question.id);
      questionBreakUpById[question.id] = toQuestionInfo(question);
    });
  }
  return { qids, questionBreakUpById };
};
export const toAnswerInfoList = answerList => {
  const aids = [];
  const answersBreakUpById = {};
  if (answerList.length) {
    answerList.forEach(answer => {
      aids.push(answer.id);
      answersBreakUpById[answer.id] = toAnswerInfo(answer);
    });
  }
  return { aids, answersBreakUpById };
};

export const filterQuestionInfo = (qid: string): IQuestionInfo => {
  const { qids, questionBreakUpById } = state;
  if (qid && qids.length) {
    const questionFilter = questionBreakUpById[qid];
    return questionFilter ? questionFilter : ({} as IQuestionInfo);
  }
  return {} as IQuestionInfo;
};

export const getQuestionListData = async ({ productId, sortId, cid, textToSearch, language, isFirst }) => {
  try {
    state.isQnaLoading = true;
    state.isQnaLoaded = false;
    const data = await getQuestionList(productId, {
      limit: 3,
      textToSearch,
      sortId,
      cid: cid || '',
      npt: 1,
      languageCode: language,
    });


    const { items = [], totalCount = 0 } = data as IQuestionList;
    const { qids, questionBreakUpById } = toQuestionInfoList(items);
    state.qids = qids;
    state.questionBreakUpById = questionBreakUpById;
    if (qids.length) {
      try {
        state.answeredList =
          ((await getAnswered(qids)) as {
            isAnswered: boolean;
            questionId: string;
          }[]) || [];
        await getQuestionListThumbStatusData();
      } catch (error) {}
    }

    state.qnaListTotalCount = totalCount;
    if (isFirst) {
      state.qnaListStaticTotalCount = totalCount;
    }
    state.isFirstLoad = isFirst;
    state.isQnaLoading = false;
    if (textToSearch) {
      addDataLayer({
        event: 'custom_event',
        name: 'ugc_interaction',
        category: 'ugc',
        action: 'qna search',
        label: textToSearch,
      });
    }
    state.isQnaLoaded = true;
  } catch (e) {
    state.isQnaLoading = false;
    state.isQnaLoaded = false;
  }
};

export const requestListData = () => {
  const {
    sortId,
    cid,
    textToSearch,
    productInfo: { productId },
    preference: { language },
  } = state;
  getQuestionListData({ productId, sortId, cid, textToSearch, language, isFirst: false }); // question list
};

export const getQuestionListThumbStatusData = async () => {
  const { qids, questionBreakUpById } = state;
  if (qids.length) {
    try {
      const answersId = qids.map(qid => questionBreakUpById[qid].answersIdList.filter(Boolean)).flat();
      const data = await getThumbStatus(answersId);
      const newQuestionBreakUpById = JSON.parse(JSON.stringify(questionBreakUpById));
      if (data && data.length) {
        data.forEach(({ id, vote }) => {
          qids.forEach(qid => {
            if (newQuestionBreakUpById[qid].answersIdList.includes(id)) {
              newQuestionBreakUpById[qid].answersDetail[id].hasThumbsUp = vote;
            }
          });
        });
      }
      state.questionBreakUpById = newQuestionBreakUpById;
    } catch (error) {}
  }
};
